import type { Vec3 } from "@rivalwatch/shared";

/**
 * Rivalwatch audio engine: one AudioContext, a small mixing desk and a spatializer.
 *
 *   world  sfx ─┬─► duck ─► muffle ─┐
 *          amb ─┤                    │
 *   reverb send ─► Foundry IR ───────┤
 *   feedback ────────────────────────┼─► glue comp ─► limiter ─► soft clip ─► master
 *   music ─► duck ───────────────────┤
 *   voice ─► PA (EQ, comp, slapback) ┤
 *   ui ──────────────────────────────┘
 *
 * Cues are plain functions that render into an {@link Out} (context + input node + start time),
 * so the same code plays live, spatialized in the match, or offline in the audio lab.
 */

export type BusName = "sfx" | "amb" | "feedback" | "music" | "voice" | "ui";

export interface Out {
  ctx: BaseAudioContext;
  dest: AudioNode;
  t: number;
}

/** A cue renders into `o` and returns how long it rings, in seconds. */
export type Cue = (o: Out) => number;

export interface AudioSettings {
  master: number;
  effects: number;
  voice: number;
  music: number;
}

export const DEFAULT_SETTINGS: AudioSettings = { master: 0.8, effects: 1, voice: 1, music: 0.8 };
const STORE_KEY = "rw.audio";

/** Base level of each bus before the player's volume sliders. */
const MIX: Record<BusName, number> = { sfx: 1, amb: 0.7, feedback: 0.9, music: 1, voice: 1, ui: 0.55 };
/** Concurrent voice budget per bus. */
const BUDGET: Record<BusName, number> = { sfx: 56, amb: 10, feedback: 14, music: 16, voice: 4, ui: 8 };
const HRTF_VOICES = 6;

export interface Placement {
  bus?: BusName;
  /** World position; omit for 2D (listener-locked) cues. */
  at?: Vec3;
  gain?: number;
  /** 2D stereo position, -1..1. */
  pan?: number;
  /** Distance at which a spatial cue plays at full level. */
  ref?: number;
  rolloff?: number;
  /** Spatial cues beyond this distance aren't created. */
  range?: number;
  /** Reverb send, 0..1. */
  wet?: number;
  /** Behind a wall: dull and quieter. */
  occluded?: boolean;
  /** 0..10. Low-priority cues are dropped first when a bus is crowded. */
  priority?: number;
  /** Start this many seconds from now. */
  delay?: number;
}

interface Placed {
  out: Out;
  input: GainNode;
  nodes: AudioNode[];
  panner?: PannerNode;
  binaural: boolean;
}

export interface LoopVoice {
  /** Stop all sources at (context) time `t`. */
  stop(t: number): void;
  /** Continuous 0..1 control (capture progress, fuel, heart rate…). */
  set?(v: number, t: number): void;
}

export interface Loop {
  readonly alive: boolean;
  /** Fade to a level (0 = silent but running). */
  level(g: number, tau?: number): void;
  move(p: Vec3): void;
  set(v: number): void;
  stop(fade?: number): void;
}

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const dist = (a: Vec3, b: Vec3) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

export class AudioEngine {
  ctx?: AudioContext;
  settings: AudioSettings;
  private bus = {} as Record<BusName, GainNode>;
  private duckWorld?: GainNode;
  private duckMusic?: GainNode;
  private muffle?: BiquadFilterNode;
  private reverbIn?: GainNode;
  private masterGain?: GainNode;
  private voices: { bus: BusName; from: number; until: number }[] = [];
  /** End times of voices using HRTF panning: it costs ~2% of a core per voice, so it's rationed. */
  private readonly hrtf: number[] = [];
  /** Rendering into an OfflineAudioContext (the audio lab): no wall-clock cleanup. */
  private offline = false;
  /** Offline only: finished chains waiting for {@link reap}, which disconnects them at audio time. */
  private graveyard: { at: number; nodes: AudioNode[] }[] = [];
  private throttles = new Map<string, number>();
  private buffers = new Map<string, Promise<AudioBuffer | undefined>>();
  private muffleHz = 20000;
  /** Listener pose, mirrored in JS for culling and manual panning. */
  readonly listener = { pos: { x: 0, y: 0, z: 0 } as Vec3, fwd: { x: 0, y: 0, z: -1 } as Vec3 };

  constructor() {
    this.settings = { ...DEFAULT_SETTINGS };
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) this.settings = { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<AudioSettings>) };
    } catch {
      /* private mode or corrupt value: defaults */
    }
  }

  /** Create/resume the context. Must run inside a user gesture the first time. */
  resume() {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor({ latencyHint: "interactive" });
      this.build(this.ctx);
    }
    if (this.ctx.state !== "running") void this.ctx.resume();
  }

  get running() {
    return this.offline || this.ctx?.state === "running";
  }

  /** Bind to an existing context instead of creating one (offline renders in the audio lab). */
  useContext(ctx: BaseAudioContext) {
    this.ctx = ctx as AudioContext;
    this.offline = typeof OfflineAudioContext !== "undefined" && ctx instanceof OfflineAudioContext;
    this.build(ctx);
  }

  get now() {
    return this.ctx?.currentTime ?? 0;
  }

  setSettings(s: Partial<AudioSettings>) {
    this.settings = { ...this.settings, ...s };
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(this.settings));
    } catch {
      /* ignore */
    }
    this.applyVolumes();
  }

  // ------------------------------------------------------------------ graph

  private build(ctx: BaseAudioContext) {
    const gain = (v = 1) => {
      const g = ctx.createGain();
      g.gain.value = v;
      return g;
    };
    const pre = gain();
    const rumble = ctx.createBiquadFilter();
    rumble.type = "highpass";
    rumble.frequency.value = 30;
    rumble.Q.value = 0.6;
    const glue = ctx.createDynamicsCompressor();
    glue.threshold.value = -18;
    glue.knee.value = 10;
    glue.ratio.value = 2.5;
    glue.attack.value = 0.008;
    glue.release.value = 0.18;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -3;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.1;
    const clip = ctx.createWaveShaper();
    clip.curve = softClip();
    clip.oversample = "2x";
    this.masterGain = gain();
    pre.connect(rumble).connect(glue).connect(limiter).connect(clip).connect(this.masterGain).connect(ctx.destination);

    for (const b of Object.keys(MIX) as BusName[]) this.bus[b] = gain();

    // World: effects + ambience + reverb return, ducked under the announcer, muffled on death.
    this.duckWorld = gain();
    this.muffle = ctx.createBiquadFilter();
    this.muffle.type = "lowpass";
    this.muffle.frequency.value = 20000;
    this.muffle.Q.value = 0.5;
    this.bus.sfx.connect(this.duckWorld);
    this.bus.amb.connect(this.duckWorld);
    const mud = ctx.createBiquadFilter();
    mud.type = "peaking";
    mud.frequency.value = 280;
    mud.Q.value = 0.9;
    mud.gain.value = -2.5;
    this.duckWorld.connect(mud).connect(this.muffle).connect(pre);

    this.reverbIn = gain();
    const verb = ctx.createConvolver();
    verb.buffer = foundryImpulse(ctx);
    const verbOut = gain(0.85);
    this.reverbIn.connect(verb).connect(verbOut).connect(this.duckWorld);

    this.bus.feedback.connect(pre);
    this.bus.ui.connect(pre);
    this.duckMusic = gain();
    this.bus.music.connect(this.duckMusic).connect(pre);
    const musicVerb = gain(0.18);
    this.bus.music.connect(musicVerb).connect(this.reverbIn);

    // Announcer over the Foundry PA: band-limited, compressed, with a slapback off the far wall.
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 160;
    const presence = ctx.createBiquadFilter();
    presence.type = "peaking";
    presence.frequency.value = 3000;
    presence.Q.value = 0.9;
    presence.gain.value = 3.5;
    const top = ctx.createBiquadFilter();
    top.type = "lowpass";
    top.frequency.value = 9000;
    const vcomp = ctx.createDynamicsCompressor();
    vcomp.threshold.value = -24;
    vcomp.ratio.value = 4;
    vcomp.attack.value = 0.003;
    vcomp.release.value = 0.12;
    const vout = gain(2);
    this.bus.voice.connect(hp).connect(presence).connect(top).connect(vcomp).connect(vout).connect(pre);
    const slap = ctx.createDelay(1);
    slap.delayTime.value = 0.14;
    const slapTone = ctx.createBiquadFilter();
    slapTone.type = "bandpass";
    slapTone.frequency.value = 1400;
    slapTone.Q.value = 0.6;
    const slapGain = gain(0.2);
    const slapFb = gain(0.22);
    vout.connect(slap).connect(slapTone).connect(slapGain).connect(pre);
    slapTone.connect(slapFb).connect(slap);
    const voiceVerb = gain(0.12);
    vout.connect(voiceVerb).connect(this.reverbIn);

    this.applyVolumes();
  }

  private applyVolumes() {
    if (!this.ctx || !this.masterGain) return;
    const s = this.settings;
    const t = this.ctx.currentTime;
    const vol: Record<BusName, number> = { sfx: s.effects, amb: s.effects, feedback: s.effects, ui: s.effects, music: s.music, voice: s.voice };
    for (const b of Object.keys(MIX) as BusName[]) this.bus[b].gain.setTargetAtTime(MIX[b] * vol[b] ** 1.6, t, 0.03);
    this.masterGain.gain.setTargetAtTime(s.master ** 1.6, t, 0.03);
  }

  // ------------------------------------------------------------------ mix state

  /** Pull the world and music down under something that must be heard (announcer, enemy ult). */
  duck(depthDb: number, hold: number, music = true, at?: number) {
    if (!this.ctx) return;
    const t = at ?? this.ctx.currentTime;
    const k = 10 ** (-depthDb / 20);
    for (const g of [this.duckWorld, music ? this.duckMusic : undefined]) {
      if (!g) continue;
      const p = g.gain;
      p.cancelScheduledValues(t);
      p.setValueAtTime(p.value, t);
      p.setTargetAtTime(Math.min(p.value, k), t, 0.015);
      p.setTargetAtTime(1, t + hold, 0.12);
    }
  }

  /** Low-pass the world (death, low health, sleep). 20 kHz = open. */
  setMuffle(hz: number, tau = 0.12) {
    if (!this.ctx || !this.muffle || Math.abs(hz - this.muffleHz) < 1) return;
    this.muffleHz = hz;
    this.muffle.frequency.setTargetAtTime(hz, this.ctx.currentTime, tau);
  }

  setListener(pos: Vec3, fwd: Vec3) {
    this.listener.pos = pos;
    this.listener.fwd = fwd;
    const ctx = this.ctx;
    if (!ctx) return;
    const l = ctx.listener;
    const t = ctx.currentTime;
    if (l.positionX) {
      l.positionX.setTargetAtTime(pos.x, t, 0.01);
      l.positionY.setTargetAtTime(pos.y, t, 0.01);
      l.positionZ.setTargetAtTime(pos.z, t, 0.01);
      l.forwardX.setTargetAtTime(fwd.x, t, 0.01);
      l.forwardY.setTargetAtTime(fwd.y, t, 0.01);
      l.forwardZ.setTargetAtTime(fwd.z, t, 0.01);
      l.upX.value = 0;
      l.upY.value = 1;
      l.upZ.value = 0;
    } else {
      l.setPosition(pos.x, pos.y, pos.z);
      l.setOrientation(fwd.x, fwd.y, fwd.z, 0, 1, 0);
    }
  }

  distanceTo(p: Vec3) {
    return dist(p, this.listener.pos);
  }

  /** -1 (hard left) .. 1 (hard right) for a world position, from the listener's view. */
  panOf(p: Vec3) {
    const f = this.listener.fwd;
    const dx = p.x - this.listener.pos.x;
    const dz = p.z - this.listener.pos.z;
    const len = Math.hypot(dx, dz) || 1;
    const fl = Math.hypot(f.x, f.z) || 1;
    // Right of forward (x,z) is (-z, x) in this coordinate system.
    return Math.max(-1, Math.min(1, ((dx / len) * (-f.z / fl) + (dz / len) * (f.x / fl)) * 0.85));
  }

  /** True at most once per `interval` seconds for a key (footsteps, hit ticks…). */
  throttle(key: string, interval: number) {
    const now = performance.now() / 1000;
    const last = this.throttles.get(key) ?? -1e9;
    if (now - last < interval) return false;
    this.throttles.set(key, now);
    if (this.throttles.size > 512) this.throttles.clear();
    return true;
  }

  // ------------------------------------------------------------------ playback

  private admit(bus: BusName, priority: number, at: number) {
    const now = this.now;
    this.voices = this.voices.filter((v) => v.until > now);
    const n = this.voices.reduce((k, v) => k + (v.bus === bus && v.from <= at + 0.001 && v.until > at ? 1 : 0), 0);
    const cap = BUDGET[bus];
    if (n >= cap) return priority >= 8;
    if (n >= cap * 0.7) return priority >= 4;
    return true;
  }

  private place(p: Placement): Placed | undefined {
    const ctx = this.ctx;
    if (!ctx || !this.running || !this.reverbIn) return undefined;
    const bus = p.bus ?? "sfx";
    const start = ctx.currentTime + 0.004 + (p.delay ?? 0);
    let d = 0;
    if (p.at) {
      d = this.distanceTo(p.at);
      if (d > (p.range ?? 70)) return undefined;
    }
    if (!this.admit(bus, p.priority ?? 5, start)) return undefined;
    const input = ctx.createGain();
    input.gain.value = p.gain ?? 1;
    const nodes: AudioNode[] = [input];
    let head: AudioNode = input;
    if (p.occluded) {
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 850;
      lp.Q.value = 0;
      const g = ctx.createGain();
      g.gain.value = 0.5;
      head.connect(lp).connect(g);
      nodes.push(lp, g);
      head = g;
    }
    let panner: PannerNode | undefined;
    if (p.at) {
      if (d > 16) {
        // Air absorption: distant fights lose their top end, not just their level.
        const air = ctx.createBiquadFilter();
        air.type = "lowpass";
        air.frequency.value = Math.max(2400, 18000 * (16 / d) ** 1.1);
        air.Q.value = 0;
        head.connect(air);
        nodes.push(air);
        head = air;
      }
      panner = ctx.createPanner();
      // HRTF (front/back, height) is costly per source: spend it on a few close, important sounds.
      for (let i = this.hrtf.length - 1; i >= 0; i--) if (this.hrtf[i]! <= start) this.hrtf.splice(i, 1);
      const binaural = d < 14 && (p.priority ?? 5) >= 5 && this.hrtf.length < HRTF_VOICES;
      panner.panningModel = binaural ? "HRTF" : "equalpower";
      panner.distanceModel = "inverse";
      panner.refDistance = p.ref ?? 3;
      panner.rolloffFactor = p.rolloff ?? 1.1;
      panner.maxDistance = 10000;
      setPannerPos(panner, p.at);
      head.connect(panner).connect(this.bus[bus]);
      nodes.push(panner);
    } else if (p.pan) {
      const sp = ctx.createStereoPanner();
      sp.pan.value = p.pan;
      head.connect(sp).connect(this.bus[bus]);
      nodes.push(sp);
    } else head.connect(this.bus[bus]);
    const wet = p.wet ?? (p.at ? 0.22 : 0);
    if (wet > 0) {
      const send = ctx.createGain();
      send.gain.value = wet * (p.at ? 1 / (1 + d / 28) : 1);
      head.connect(send).connect(this.reverbIn);
      nodes.push(send);
    }
    return { out: { ctx, dest: input, t: start }, input, nodes, panner, binaural: panner?.panningModel === "HRTF" };
  }

  private retire(nodes: AudioNode[], from: number, seconds: number, bus: BusName) {
    this.voices.push({ bus, from, until: from + seconds });
    if (this.offline) this.graveyard.push({ at: from + seconds, nodes });
    else setTimeout(() => nodes.forEach((n) => n.disconnect()), (from - this.now + seconds) * 1000);
  }

  /** Offline renders: disconnect every chain that finished before audio time `t`, as live play would. */
  reap(t: number) {
    const keep: typeof this.graveyard = [];
    for (const g of this.graveyard) {
      if (g.at <= t) g.nodes.forEach((n) => n.disconnect());
      else keep.push(g);
    }
    this.graveyard = keep;
  }

  /** Play a one-shot cue. Returns false if it was culled (too far, bus full, audio off). */
  play(p: Placement, cue: Cue): boolean {
    const placed = this.place(p);
    if (!placed) return false;
    const dur = cue(placed.out);
    if (placed.binaural) this.hrtf.push(placed.out.t + dur + 0.1);
    this.retire(placed.nodes, placed.out.t, dur + 0.1, p.bus ?? "sfx");
    return true;
  }

  /** Start a continuous voice (engine hum, jets, music bed). Starts at level 0 unless `level` given. */
  loop(p: Placement, build: (o: Out) => LoopVoice, level = 1, fadeIn = 0.15): Loop | undefined {
    const placed = this.place({ ...p, priority: p.priority ?? 7 });
    if (!placed) return undefined;
    const ctx = placed.out.ctx as AudioContext;
    const voice = build(placed.out);
    const g = placed.input.gain;
    const base = p.gain ?? 1;
    g.setValueAtTime(0, placed.out.t);
    g.setTargetAtTime(base * level, placed.out.t, fadeIn / 3);
    let alive = true;
    const offline = this.offline;
    // A binaural loop holds its HRTF slot until it stops.
    const hrtf = this.hrtf;
    const binaural = placed.binaural;
    if (binaural) hrtf.push(Number.POSITIVE_INFINITY);
    const bus = p.bus ?? "sfx";
    const slot = { bus, from: placed.out.t, until: Number.POSITIVE_INFINITY };
    this.voices.push(slot);
    return {
      get alive() {
        return alive;
      },
      level: (v, tau = 0.08) => {
        if (alive) g.setTargetAtTime(base * v, ctx.currentTime, tau);
      },
      move: (pos) => {
        if (alive && placed.panner) setPannerPos(placed.panner, pos, ctx.currentTime);
      },
      set: (v) => {
        if (alive) voice.set?.(clamp01(v), ctx.currentTime);
      },
      stop: (fade = 0.25) => {
        if (!alive) return;
        alive = false;
        const t = ctx.currentTime;
        g.cancelScheduledValues(t);
        g.setValueAtTime(g.value, t);
        g.setTargetAtTime(0, t, fade / 4);
        voice.stop(t + fade + 0.05);
        slot.until = t + fade;
        if (binaural) {
          const i = hrtf.indexOf(Number.POSITIVE_INFINITY);
          if (i >= 0) hrtf[i] = t + fade;
        }
        if (!offline) setTimeout(() => placed.nodes.forEach((n) => n.disconnect()), (fade + 0.8) * 1000);
      },
    };
  }

  // ------------------------------------------------------------------ samples

  buffer(url: string, quiet = false): Promise<AudioBuffer | undefined> {
    let b = this.buffers.get(url);
    if (!b) {
      b = fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`${r.status}`))))
        .then((data) => (this.ctx ?? new OfflineAudioContext(1, 1, 44100)).decodeAudioData(data))
        .catch((e) => {
          if (!quiet) console.warn("audio: could not load", url, e);
          return undefined;
        });
      this.buffers.set(url, b);
    }
    return b;
  }

  /** Play a decoded sample. Resolves to its duration, or 0 if it didn't play. */
  async sample(p: Placement, url: string, rate = 1, quiet = false): Promise<number> {
    const buf = await this.buffer(url, quiet);
    if (!buf) return 0;
    let dur = 0;
    const ok = this.play(p, (o) => {
      const src = o.ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = rate;
      src.connect(o.dest);
      src.start(o.t);
      dur = buf.duration / rate;
      return dur;
    });
    return ok ? dur : 0;
  }
}

function setPannerPos(p: PannerNode, v: Vec3, t?: number) {
  if (p.positionX) {
    if (t === undefined) {
      p.positionX.value = v.x;
      p.positionY.value = v.y;
      p.positionZ.value = v.z;
    } else {
      p.positionX.setTargetAtTime(v.x, t, 0.02);
      p.positionY.setTargetAtTime(v.y, t, 0.02);
      p.positionZ.setTargetAtTime(v.z, t, 0.02);
    }
  } else p.setPosition(v.x, v.y, v.z);
}

let clipCurve: Float32Array<ArrayBuffer> | undefined;
/** Transparent below -3 dBFS, then rounds peaks off instead of clipping. */
function softClip() {
  if (clipCurve) return clipCurve;
  const n = 2048;
  clipCurve = new Float32Array(n);
  const knee = 0.7;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const a = Math.abs(x);
    clipCurve[i] = a <= knee ? x : Math.sign(x) * (knee + (1 - knee) * Math.tanh((a - knee) / (1 - knee)));
  }
  return clipCurve;
}

/**
 * The Foundry: a steel hall. Pre-delay, a flutter of hard early reflections off sheet metal,
 * then a 1.5 s tail that darkens as it decays.
 */
export function foundryImpulse(ctx: BaseAudioContext, seconds = 2.4) {
  const sr = ctx.sampleRate;
  const len = Math.floor(sr * seconds);
  const ir = ctx.createBuffer(2, len, sr);
  const rt60 = 1.5;
  const pre = 0.012;
  for (let ch = 0; ch < 2; ch++) {
    const d = ir.getChannelData(ch);
    let lp = 0;
    for (let i = Math.floor(pre * sr); i < len; i++) {
      const t = i / sr - pre;
      const env = Math.exp((-6.91 * t) / rt60);
      const damp = 0.25 + 0.7 * Math.min(1, t / 1.1);
      lp += (Math.random() * 2 - 1 - lp) * (1 - damp);
      d[i] = lp * env * 0.6;
    }
    // Early reflections: hard, slightly metallic taps that differ per ear.
    for (let k = 0; k < 14; k++) {
      const at = pre + 0.004 + k * (0.0115 + ch * 0.0017) + Math.random() * 0.003;
      const amp = (0.9 - k * 0.055) * (Math.random() < 0.5 ? -1 : 1);
      const i0 = Math.floor(at * sr);
      for (let j = 0; j < 90 && i0 + j < len; j++) d[i0 + j]! += amp * Math.exp(-j / 14) * (Math.random() * 0.6 + 0.4);
    }
  }
  return ir;
}

/** The shared engine. UI and game both play through it. */
export const audio = new AudioEngine();
